import { forkJoin, iif as observableIif, Observable, of } from 'rxjs';
import { Component, OnInit, ViewChild } from '@angular/core';
import { ActivatedRoute, Params } from '@angular/router';
import { UserInfo } from '../shared/user-info.model';
import { LoaderService } from '../../shared/services/loader.service';
import { DisplayMode } from '../../shared/display-mode';
import { ToastrService } from 'ngx-toastr';
import { CarrierManagerService } from '../shared/carrier-manager.service';
import { HttpErrorResponse } from '@angular/common/http';
import { CarrierService } from '../shared/carrier.service';
import { ComponentCanDeactivate } from '../../core/guards/pending-changes.guard';
import { UserFormComponent } from '../user-form/user-form.component';
import { NavigationService } from '../../shared/services/navigation.service';
import { catchError, finalize, tap } from 'rxjs/operators';
import { BluechatService } from '../../bluechat/bluechat.service';
import { BluechatUserCreation } from '../../bluechat/models/BluechatUserCreation';
import { ModalService } from '../../shared/services/modal.service';
import { ErrorHandlerService } from '../../shared/services/error-handler.service';
import { UserInfoWithNames } from '../user-table/user-table.component';
import { TranslateService } from '@ngx-translate/core';

@Component({
  selector: 'app-carrier-manager-form',
  templateUrl: './carrier-manager-form.component.html',
  styleUrls: ['./carrier-manager-form.component.scss']
})
export class CarrierManagerFormComponent implements OnInit, ComponentCanDeactivate {

  @ViewChild('userForm') userFormComponent: UserFormComponent;

  public onCreate: (u: UserInfo) => Observable<UserInfo>;
  public onUpdate: (u: UserInfo) => Observable<UserInfo>;
  public onDelete: (u: UserInfo) => Observable<boolean>;
  public onToggleBlocking: (u: UserInfo) => Observable<boolean>;
  public onUnblockIp: (u: UserInfo) => Observable<boolean>;

  public mode = DisplayMode.Creation;
  public DisplayMode = DisplayMode;
  public formTitle = 'New Carrier Manager';
  public isBluechatActivatedOnCarrier = false;
  public user = new UserInfo();
  public currentUserIsCarrierManager: boolean;

  constructor(private route: ActivatedRoute,
              private navigation: NavigationService,
              private carrierService: CarrierService,
              private carrierManagerService: CarrierManagerService,
              private loader: LoaderService,
              private bluechatService: BluechatService,
              private modalService: ModalService,
              private toastr: ToastrService,
              private errorHandler: ErrorHandlerService,
              private translate: TranslateService,
  ) {
    this.onCreate = (u => {
      return this.carrierManagerService.create(u);
    });

    this.onUpdate = (u => {
      return this.carrierManagerService.update(u);
    });

    this.onDelete = (u => {
      return this.carrierManagerService.delete(u.id);
    });

    this.onToggleBlocking = (u => {
      return this.carrierManagerService.toggleBlocking(u);
    });

    this.onUnblockIp = (u => {
      return this.carrierManagerService.unblockIp(u);
    });
  }

  public ngOnInit() {
    this.route.params.subscribe((params: Params): void => {
      this.currentUserIsCarrierManager = !params.carrierId;
      const carrierId = params.carrierId;
      const managerId = params.managerId;

      this.loader.startLoad(false);

      const carrierObservable =
          observableIif(
              () => !this.currentUserIsCarrierManager,
              this.carrierService.getById(carrierId)
                  .pipe(
                      catchError((error: HttpErrorResponse) => {
                        console.log(error);
                        this.toastr.error(
                            error.error.message || 'An error occured while fetching the carrier, redirecting to carrier list',
                            'Error'
                        );
                        this.navigation.carriers()
                            .list()
                            .go();

                        return of(null);
                      })
                  ),
              this.carrierService.getSelfCarrier()
                  .pipe(
                      catchError((error: HttpErrorResponse) => {
                        console.log(error);
                        this.toastr.error(
                            error.error.message || 'An error occured while fetching the carrier, redirecting to carrier list',
                            'Error'
                        );
                        this.navigation.carriers()
                            .list()
                            .go();

                        return of(null);
                      })
                  )
          );



      const carrierManagerObservable = observableIif(() => !!managerId,
        this.carrierManagerService.getById(managerId).pipe(
          tap(() => this.switchToUpdateMode()),
          catchError((error: HttpErrorResponse) => {
            console.log(error);
            this.toastr.error(error.error.message || 'An error occured while fetching the user', 'Error');

            return of(new UserInfo());
          })
        ),
        of(new UserInfo())
      );

      forkJoin([carrierObservable, carrierManagerObservable])
        .pipe(finalize(() => this.loader.stopLoad()))
        .subscribe(([carrier, manager]) => {
          this.user = manager;

          if (carrier) {
            this.user.carrier                 = carrier;
            this.isBluechatActivatedOnCarrier = this.user.carrier ? this.user.carrier.bluechatEnabled() : false;
          }
        });
    });
  }

  public getForm() {
    return this.userFormComponent.form;
  }

  public switchToUpdateMode(): void {
    this.mode      = DisplayMode.Edition;
    this.formTitle = 'Carrier Manager Edition';
  }

  public goToManagerList() {
      this.currentUserIsCarrierManager
          ? this.navigation.selfCarrier().carrierManager().list().go()
          : this.navigation.carriers().managers(this.user.carrier.id).list().go();
  }

  public changePassword(password: string) {
    this.loader.startLoad();
    this.carrierManagerService.updatePassword(this.user, password)
      .pipe(finalize(() => this.loader.stopLoad()))
      .subscribe(() => {
        this.toastr.success('The password has been successfully updated', 'Success');
      }, (error: HttpErrorResponse) => {
        console.log(error);
        this.toastr.error(error.error.message || 'Error while updating the password', 'Error');
      });
  }

  public enableBluechat(userDetails: UserInfoWithNames) {
    this.loader.startLoad();
    this.bluechatService.enableBluechatOnCarrierManager(userDetails.user.id, this.createBluechatUserCreationRequest(userDetails))
      .pipe(
        finalize(() => this.loader.stopLoad()),
      )
      .subscribe(user => {
          this.toastr.success(this.translate.instant('bluechat.activated'));
          this.user = user;
      }, err => {
        this.errorHandler.handleHttpError(err)
      });
  }

  private createBluechatUserCreationRequest(userInfo: UserInfoWithNames): BluechatUserCreation {
    return {
      bluechat_carrier_code: userInfo.user.carrier.bluechatCarrierCode,
      firstname: userInfo.firstname,
      lastname: userInfo.lastname,
      email: userInfo.user.email,
    };
  }
}
